import React, {state, useState, useRef} from "react";
import '@fontsource/montserrat'
import { ReactSketchCanvas } from "react-sketch-canvas";
import {
	Button,
	Box,
    Link,
    TextField,
    Typography,
	Checkbox,
	FormControlLabel,
	FormHelperText,
	FormControl
} from "@mui/material";
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import styles from "./AccessCode.module.css"
import logo from "../../img/logo-small.png"
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PDFDocument } from 'pdf-lib';


const AccessCode = () => {

	const { control, register, getValues, setError, clearErrors, formState: { errors } } = useForm();
	const navigate = useNavigate();

	const handleValidation = async () => {
		const value = getValues("accessCode");
		const check = getValues("hasAgreed");

		clearErrors(["accessCode", "hasAgreed"]);

		let isValid = true;

		// if (value !== correctCode) {
		// 	setError("accessCode", {
		// 		type: "manual",
		// 		message: "The access code you entered is incorrect.",
		// 	});
		// 	isValid = false;
		// }

		if (!check) {
			setError("hasAgreed", {
				type: "manual",
				message: "Please agree to the Terms of Service and Privacy Policy",
			});
			isValid = false;
		}

		if (isValid) {
			try {
				const response = await fetch(`/api/verify-participant/${value}`);
				const result = await response.json();

				if (!result.success) {
					const errorMessage = result.alreadyUsed 
                    ? "This code has been used to fill out a form already" 
                    : "The access code you entered does not exist in our records.";

					setError("accessCode", {
						type: "manual",
						message: errorMessage,
					});
					isValid = false;
				}
			} catch (error) {
				console.error("Verification error:", error);
				setError("accessCode", {
					type: "manual",
					message: "Unable to reach the server. Please try again later.",
				});
				isValid = false;
			}
		}

			if (isValid) {
				// navigate('/form', { state: { accessCode: value } });
				sessionStorage.setItem('currentAccessCode', value);
    			navigate('/form', {replace:true});
			}
	};

	const buttonGradient = 'linear-gradient(to right, #CA7400, #B12F6B, #8B579E)';

    const buttonStyle ={
    	borderRadius: 999,                 
    	border: '1px solid transparent',
		textTransform: 'none',  
    	color: '#F8F5FE',
    	fontSize:'14px',
        whiteSpace: "nowrap",
		width: "476px",
		height: "52px",
		backgroundClip: 'padding-box, border-box',
    	backgroundOrigin: 'border-box',
    	backgroundImage: `linear-gradient(#13111B, #13111B), ${buttonGradient}`,
		boxShadow: 'none',
		position: 'relative',
		isolation: 'isolate',
		
		"& .MuiButton-endIcon": {
			position: 'relative',
			zIndex: 2, 
		},

		'&::after': {
			content: '""',
			position: 'absolute',
			inset: '1px',          
			background: '#13111B',  
			borderRadius: "999px",
			zIndex: 0,              
    	},

		'&::before': {
			content: '""', 
			position: 'absolute',
			zIndex: -1,         
			top: '-6px',
			left: '-6px',
			right: '-6px',
			bottom: '-6px',
      
			background: buttonGradient,
			borderRadius: 999,
			
			filter: 'blur(16px)', 
			opacity: 0.6, 
    	},

    	'&:hover': {
			'&::after': {
            	opacity: 0, 
        	},
			'&::before': {
				opacity: 1,
				filter: 'blur(20px)',
        	},
    		borderColor: 'none',
      		background: buttonGradient,
			boxShadow: 'none', 
    	}
    }

	const textboxStyle={
		width: "476px",
		height: "52px",
		//This is for styling the base textfield
		"& .MuiOutlinedInput-root": {
			backgroundColor: "#1C1A25",
			color: "#F8F5FE",
			borderRadius:"16px",

		//This is for styling the default border
			'& fieldset': {
				borderColor: '#302C3F',
				borderWidth: '1px', 
			},
		// //This is for styling fieldset on hover
		 	'&:hover fieldset': {
		 		borderColor: '#302C3F',
				borderWidth: '1px'
		 	},
		// This is for styling the fieldset when clicked
		 '&.Mui-focused fieldset': {
		 	borderColor: '#302C3F',
			borderWidth: '1px'
		 	},
		 },
		// This is for styling the input text
		"& .MuiInputBase-input": {
			color: "white", 
		},
		
	}

	const canvasRef = useRef(null);

	const handleSeeAgreement = async (fileName) => {
		try {
			if (fileName) {
				const response = await fetch(`/${fileName}`);
				if (!response.ok) throw new Error(`Could not find ${fileName} in public folder`);
				
				const existingPdfBytes = await response.arrayBuffer();
				const pdfDoc = await PDFDocument.load(existingPdfBytes);
				
				const pdfBytes = await pdfDoc.save();
				const blob = new Blob([pdfBytes], { type: 'application/pdf' });
				const url = URL.createObjectURL(blob);
				
				window.open(url, '_blank');
				return; 
			}

			if (!canvasRef.current) return;
			const signatureImageBase64 = await canvasRef.current.exportImage("png");

		} catch (err) {
			console.error("PDF Error:", err);
			alert("Error: " + err.message);
		}
	};

    return (
        <div className={styles.background}>
            
            <div className={styles.mainBox}>
				<img src={logo} alt="Loading Error" className={styles.logo}></img>
				<div className={styles.midBox}>
					<div className={styles.bigText}>
						Enter Your Access Code
					</div>
					<div className={styles.smallText}>
						Please enter the unique access code provided to you by Regional Dance America (RDA)
					</div>
				</div>
				<div className={styles.midBox} >
					<div style={{marginBottom:"52px"}}>
						<div className={styles.labelText}>Access Code</div>
                		<TextField placeholder="Enter your code" name="accessCode" sx={textboxStyle} helperText={errors.accessCode?.message} error={!!errors.accessCode} {...register("accessCode")}></TextField>
					</div>
                	<Button variant="contained" sx={buttonStyle} onClick={handleValidation} endIcon={<ArrowOutwardIcon/>}>
						<Box component="span" sx={{ position: 'relative', zIndex: 1, color:'#F8F5FE' }}>
							Continue to Registration Form
						</Box>
					</Button>
					<Controller
						name="hasAgreed"
						control={control}
						rules={{ required: "You must agree to the terms to continue" }}
						render={({ field }) => (
							<FormControlLabel
							control={
								<Checkbox {...field} 
									sx={{
										color: '#CCB2D5', 
										'&.Mui-checked': {
										color: '#B12F6B', 
										},
  										}}
  									checked={field.value} />}
							label={
								<span>
								I have read and agree to the{" "}
								<Link
									href="#"
									onClick={(e) => {
									e.stopPropagation(); 
									handleSeeAgreement("ProfilePlanetTermsofService.pdf");
									}}
									sx={{ 
										cursor: "pointer", 
										textDecoration: "underline", 
										color: '#B12F6B',            
										textDecorationColor: '#B12F6B', 
										'&:hover': {
											color: '#E04F80',          
											textDecoration: 'none',    
										},	
									}}
								>
									Terms of Service 
								</Link>
								{" "}and to the{" "}
								<Link
									href="#"
									onClick={(e) => {
									e.stopPropagation(); 
									handleSeeAgreement("ProfilePlanetPrivacyPolicy.pdf");
									}}
									sx={{ 
										cursor: "pointer", 
										textDecoration: "underline", 
										color: '#B12F6B',            
										textDecorationColor: '#B12F6B', 
										'&:hover': {
											color: '#E04F80',         
											textDecoration: 'none',    
										},	
									}}
								>
									Privacy Policy
								</Link>

								</span>
							}
							/>
						)}
						/>
						{errors.hasAgreed && (
						<FormHelperText sx={{ color: '#d32f2f', ml: 2 }}>{errors.hasAgreed.message}</FormHelperText>
						)}
				</div>
				<div>
					<div className={styles.bottomText}>Don't have an access code?</div>
					<Typography sx={{ color: '#686673', fontSize:'14px', lineHeight:'16px', marginBottom:'36px'}}>
						Contact{' '}
						<Link 
							href="mailto:programs@regionaldanceamerica.org" 
							sx={{ 
							color: '#B12F6B',       
							textDecoration: 'none', 
							'&:hover': {
								color: '#E04F80',     
								textDecoration: 'underline'
							}
							}}
						>
							programs@regionaldanceamerica.org
						</Link>
					</Typography>
				</div>
            </div>

        </div>
    );
};

export default AccessCode;